import React from 'react';
import clsx from 'clsx';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { Button, Card, CardBody, CardHeader, Textarea } from '../components/ui';
import { getFeedbackForm, getFeedbackResponse, setFeedbackResponse } from '../lib/firestore';
import { formatDateTime, toDateSafe } from '../lib/dates';
import type { FeedbackAnswerValue, FeedbackForm, FeedbackQuestion } from '../lib/types';

type Answers = Record<string, FeedbackAnswerValue>;

function isAnswered(question: FeedbackQuestion, answers: Answers) {
  const value = answers[question.id];

  if (question.type === 'text') {
    return typeof value === 'string' && value.trim().length > 0;
  }

  if (question.type === 'multi-choice') {
    return Array.isArray(value) && value.length > 0;
  }

  if (question.type === 'one-to-x') {
    const min = question.scaleMin ?? 1;
    const max = question.scaleMax ?? 5;
    return typeof value === 'number' && value >= min && value <= max;
  }

  return typeof value === 'string' && value.trim().length > 0;
}

function cleanAnswers(form: FeedbackForm, answers: Answers) {
  const next: Answers = {};

  for (const question of form.questions) {
    const value = answers[question.id];

    if (question.type === 'text') {
      if (typeof value === 'string' && value.trim()) next[question.id] = value.trim();
      continue;
    }

    if (question.type === 'multi-choice') {
      if (Array.isArray(value) && value.length) next[question.id] = value;
      continue;
    }

    if (question.type === 'one-to-x') {
      if (typeof value === 'number' && Number.isFinite(value)) next[question.id] = value;
      continue;
    }

    if (typeof value === 'string' && value.trim()) next[question.id] = value;
  }

  return next;
}

function optionClasses(active: boolean, disabled: boolean) {
  return clsx(
    'flex min-h-11 items-center gap-2 rounded-xl border px-3 py-2 text-sm transition',
    active
      ? 'border-accent bg-accent/10 text-zinc-950'
      : 'border-zinc-200 bg-white text-zinc-800',
    disabled ? 'cursor-default' : 'cursor-pointer hover:border-zinc-300 hover:bg-zinc-50'
  );
}

function scaleValues(question: FeedbackQuestion) {
  const min = question.scaleMin ?? 1;
  const max = question.scaleMax ?? 5;
  const values: number[] = [];
  for (let i = min; i <= max; i += 1) values.push(i);
  return values;
}

function maxSelectionsFor(question: FeedbackQuestion) {
  return typeof question.maxSelections === 'number' && question.maxSelections > 0
    ? Math.floor(question.maxSelections)
    : null;
}

function QuestionField(props: {
  question: FeedbackQuestion;
  index: number;
  value: FeedbackAnswerValue | undefined;
  disabled: boolean;
  onChange: (value: FeedbackAnswerValue) => void;
}) {
  const { question, value } = props;

  if (question.type === 'text') {
    return (
      <Textarea
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => props.onChange(event.target.value)}
        rows={4}
        placeholder="Your feedback"
        disabled={props.disabled}
      />
    );
  }

  if (question.type === 'multi-choice') {
    const selected = Array.isArray(value) ? value : [];
    const maxSelections = maxSelectionsFor(question);
    const atLimit = maxSelections !== null && selected.length >= maxSelections;
    return (
      <div className="space-y-2">
        {maxSelections ? (
          <div className="text-xs text-zinc-500">
            Choose up to {maxSelections}. {selected.length}/{maxSelections} selected.
          </div>
        ) : null}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {(question.options ?? []).map((option) => {
            const active = selected.includes(option.id);
            const optionDisabled = props.disabled || (!active && atLimit);
            return (
              <label key={option.id} className={optionClasses(active, optionDisabled)}>
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-red-600"
                  checked={active}
                  disabled={optionDisabled}
                  onChange={() => {
                    if (!active && atLimit) return;
                    props.onChange(
                      active ? selected.filter((id) => id !== option.id) : [...selected, option.id]
                    );
                  }}
                />
                <span>{option.label}</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  }

  if (question.type === 'one-to-x') {
    const selected = typeof value === 'number' ? value : null;
    const minLabel = question.scaleMinLabel?.trim();
    const maxLabel = question.scaleMaxLabel?.trim();
    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
        {minLabel ? (
          <div className="flex min-h-11 w-16 items-center justify-end text-right text-xs leading-tight text-zinc-500 sm:w-20">
            {minLabel}
          </div>
        ) : null}
        <div className="flex flex-wrap gap-1.5 sm:gap-2">
          {scaleValues(question).map((scaleValue) => {
            const active = selected === scaleValue;
            return (
              <button
                key={scaleValue}
                type="button"
                className={clsx(
                  'flex h-11 w-11 items-center justify-center rounded-xl border text-sm font-semibold transition',
                  active
                    ? 'border-accent bg-accent text-white'
                    : 'border-zinc-200 bg-white text-zinc-800',
                  props.disabled ? 'cursor-default' : 'hover:border-zinc-300 hover:bg-zinc-50'
                )}
                disabled={props.disabled}
                onClick={() => props.onChange(scaleValue)}
              >
                {scaleValue}
              </button>
            );
          })}
        </div>
        {maxLabel ? (
          <div className="flex min-h-11 w-16 items-center text-xs leading-tight text-zinc-500 sm:w-20">
            {maxLabel}
          </div>
        ) : null}
      </div>
    );
  }

  const selected = typeof value === 'string' ? value : '';
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {(question.options ?? []).map((option) => {
        const active = selected === option.id;
        return (
          <label key={option.id} className={optionClasses(active, props.disabled)}>
            <input
              type="radio"
              className="h-4 w-4 accent-red-600"
              name={question.id}
              checked={active}
              disabled={props.disabled}
              onChange={() => props.onChange(option.id)}
            />
            <span>{option.label}</span>
          </label>
        );
      })}
    </div>
  );
}

export default function FeedbackFormPage() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = React.useState<FeedbackForm | null>(null);
  const [answers, setAnswers] = React.useState<Answers>({});
  const [submitted, setSubmitted] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);
  const [nowMs, setNowMs] = React.useState(() => Date.now());

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [formId]);

  React.useEffect(() => {
    if (!formId || !user?.sub) {
      setLoading(false);
      setForm(null);
      setAnswers({});
      setSubmitted(false);
      return;
    }

    let active = true;
    setLoading(true);
    setMessage(null);

    Promise.all([getFeedbackForm(formId), getFeedbackResponse(formId, user.sub)])
      .then(([nextForm, response]) => {
        if (!active) return;
        setForm(nextForm);
        setAnswers(response?.answers ?? {});
        setSubmitted(Boolean(response?.submittedAt));
      })
      .catch(() => {
        if (!active) return;
        setForm(null);
        setAnswers({});
        setSubmitted(false);
        setMessage('Could not load this feedback form.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [formId, user?.sub]);

  React.useEffect(() => {
    const availableAt = toDateSafe(form?.availableFrom)?.getTime();
    if (!availableAt || availableAt <= nowMs) return;

    const timeout = window.setTimeout(
      () => setNowMs(Date.now()),
      Math.min(availableAt - nowMs, 2_147_483_647)
    );
    return () => window.clearTimeout(timeout);
  }, [form?.availableFrom, nowMs]);

  const availableFromDate = toDateSafe(form?.availableFrom);
  const availableFromLabel = availableFromDate ? formatDateTime(availableFromDate) : '';
  const isFormAvailable = !availableFromDate || availableFromDate.getTime() <= nowMs;
  const fieldsDisabled = submitted || !isFormAvailable;

  function updateAnswer(questionId: string, value: FeedbackAnswerValue) {
    if (submitted || !isFormAvailable) return;
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    setMessage(null);
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!form || !user?.sub || saving || submitted) return;

    if (!isFormAvailable) {
      setMessage(`This feedback form is available from ${availableFromLabel}.`);
      return;
    }

    const missing = form.questions.filter((question) => question.required && !isAnswered(question, answers));
    if (missing.length) {
      setMessage('Please complete the required questions before submitting.');
      return;
    }

    const overSelected = form.questions.filter((question) => {
      if (question.type !== 'multi-choice') return false;
      const maxSelections = maxSelectionsFor(question);
      const value = answers[question.id];
      return Boolean(maxSelections && Array.isArray(value) && value.length > maxSelections);
    });
    if (overSelected.length) {
      setMessage('Please reduce any multi-choice answers that exceed the allowed number of options.');
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      await setFeedbackResponse({
        formId: form.id,
        profileId: user.sub,
        answers: cleanAnswers(form, answers)
      });
      setSubmitted(true);
      setMessage('Feedback submitted.');
    } catch (error: any) {
      setMessage(error?.message || 'Could not submit your feedback.');
    } finally {
      setSaving(false);
    }
  }

  function goBack() {
    if (window.history.length > 1) navigate(-1);
    else navigate('/conference');
  }

  if (loading) {
    return (
      <Card>
        <CardBody>
          <div className="text-sm text-zinc-600">Loading feedback form...</div>
        </CardBody>
      </Card>
    );
  }

  if (!form) {
    return (
      <Card>
        <CardBody>
          <div className="text-sm font-semibold text-zinc-900">Feedback form not found</div>
          <div className="mt-1 text-sm text-zinc-600">The link may be wrong or the form may no longer be active.</div>
        </CardBody>
      </Card>
    );
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-4">
      <Card className="overflow-hidden">
        <CardHeader
          title={form.title}
          subtitle={form.description}
          right={
            <Button variant="secondary" onClick={goBack}>
              Back
            </Button>
          }
        />

        <CardBody className="space-y-4">
          {!isFormAvailable ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              This feedback form is available from {availableFromLabel}.
            </div>
          ) : null}

          {isFormAvailable
            ? form.questions.map((question, index) => (
                <section key={question.id} className="rounded-xl border border-zinc-200 bg-white p-3">
                  <div className="mb-3 text-sm font-semibold text-zinc-950">
                    {index + 1}) {question.prompt}
                    {question.required ? <span className="text-accent"> *</span> : null}
                  </div>
                  <QuestionField
                    question={question}
                    index={index}
                    value={answers[question.id]}
                    disabled={fieldsDisabled}
                    onChange={(value) => updateAnswer(question.id, value)}
                  />
                </section>
              ))
            : null}

          {message ? (
            <div
              role="status"
              className={clsx(
                'rounded-xl border px-3 py-2 text-sm',
                message === 'Feedback submitted.'
                  ? 'border-green-200 bg-green-50 text-green-700'
                  : 'border-red-200 bg-red-50 text-red-700'
              )}
            >
              {message}
            </div>
          ) : null}

          {submitted ? (
            <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-700">
              Submitted responses are locked.
            </div>
          ) : isFormAvailable ? (
            <div className="flex justify-end">
              <Button type="submit" disabled={saving}>
                {saving ? 'Submitting...' : 'Submit'}
              </Button>
            </div>
          ) : (
            null
          )}
        </CardBody>
      </Card>
    </form>
  );
}
